import { useMutation } from '@tanstack/react-query'
import { App as AntdApp, Button, Flex, Form, Select, Typography } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessListQueryKey,
  organizationsAccessRetrieveQueryKey,
  organizationsAccessRolesCreateMutation,
  organizationsRolesRetrieveQueryKey
} from 'src/api/main'
import { isOwnerRole } from 'src/features/workspaces/pages/WorkspaceRolesPage/constants'
import { useWorkspaceRoles } from 'src/features/workspaces/pages/WorkspaceRolesPage/hooks'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { workspaceUsersListQueryKey } from '../hooks'
import type { WorkspaceUser } from '../types'
import { composeDisplayName, mergeManagedRoleIds } from '../utils'

const { Text } = Typography

/**
 * Role picker shared by the workspace-access card (in a popover) and the
 * members table (in a modal).
 *
 * Offers the workspace's own roles (`/organizations/workspace-roles/`) — the
 * built-in Admin and Member as well as any custom one. The tier shown on the
 * access record is `readonly` and derived from which of them the member holds,
 * so changing it means re-assigning through
 * `POST /organizations/access/{id}/roles/`. That endpoint replaces the whole
 * list, so app-scoped grants are carried over untouched.
 *
 * Owner is not offered. Handing the workspace over is a separate, owner-only
 * operation in the workspace settings danger zone.
 */
export const UserRoleAssignForm = ({
  record,
  workspaceId,
  onDone
}: {
  record: WorkspaceUser
  workspaceId: number
  onDone: () => void
}) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [form] = Form.useForm<{ role: number }>()

  const { roles, isLoading } = useWorkspaceRoles(workspaceId)

  // Every workspace role this member already holds is replaced on save; the
  // app-scoped ones say so themselves and survive the merge.
  const workspaceRoleIds = useMemo(
    () =>
      new Set(
        record.roles
          .filter((role) => role.application === null)
          .map((role) => role.id)
      ),
    [record.roles]
  )
  const options = useMemo(
    () =>
      roles
        .filter((r) => !isOwnerRole(r.codename))
        .map((r) => ({ value: r.id, label: r.name })),
    [roles]
  )
  const currentRoleId = record.roles.find(
    (role) => role.application === null
  )?.id

  const { mutateAsync, isPending } = useMutation(
    organizationsAccessRolesCreateMutation()
  )

  const submit = async ({ role }: { role: number }) => {
    const nextRoles = mergeManagedRoleIds(
      record.roles.map((r) => r.id),
      workspaceRoleIds,
      [role]
    )

    try {
      await mutateAsync({ path: { id: record.id }, body: { roles: nextRoles } })
      await invalidateQueries([
        workspaceUsersListQueryKey(workspaceId),
        organizationsAccessRetrieveQueryKey({ path: { id: record.id } }),
        organizationsAccessListQueryKey(),
        organizationsRolesRetrieveQueryKey({ path: { id: 0 } })
      ])
      message.success(t('users.modals.changeRole.success'))
      onDone()
    } catch (error) {
      showApiErrors(error, form)
    }
  }

  return (
    <Flex vertical gap={8} style={{ width: '100%' }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('users.modals.changeRole.description', {
          name: composeDisplayName(
            record.user.first_name,
            record.user.last_name,
            record.user.email
          )
        })}
      </Text>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ role: currentRoleId }}
        onFinish={submit}
      >
        <Form.Item
          name="role"
          label={t('users.modals.changeRole.fieldLabel')}
          style={{ marginBottom: 0 }}
        >
          <Select<number>
            optionFilterProp="label"
            loading={isLoading}
            options={options}
            placeholder={t('users.modals.changeRole.fieldLabel')}
          />
        </Form.Item>
      </Form>
      <Flex justify="flex-end" gap={8}>
        <Button size="small" onClick={onDone} disabled={isPending}>
          {t('users.modals.changeRole.cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          loading={isPending}
          onClick={() => form.submit()}
        >
          {t('users.modals.changeRole.submit')}
        </Button>
      </Flex>
    </Flex>
  )
}
